import { VerifiedViewer } from "@/session/product-session";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { StorefrontLoading } from "@softmax/product-ux/catalog";
import { GameRouteError } from "@/features/game/navigation";
import { frontDoorQueryOptions } from "./preview.queries";
import { PreviewPage } from "./storefront/preview-page";
import { previewHead } from "./data/preview-head";

export const previewRouteOptions = {
  loader: async ({
    context,
    location,
  }: {
    context: { queryClient: QueryClient };
    location: { href: string };
  }) => {
    const options = frontDoorQueryOptions({
      href: location.href,
    });

    if (typeof window === "undefined")
      await context.queryClient.ensureQueryData(options);
    else void context.queryClient.prefetchQuery(options);
  },
  pendingComponent: StorefrontLoading,
  errorComponent: GameRouteError,
  component: PreviewRoute,
};

function PreviewRoute() {
  const location = useLocation();
  const query = useQuery(frontDoorQueryOptions({ href: location.href }));

  if (!query.data) {
    if (query.isError)
      return (
        <main role="alert" className="p-6">
          <h1>Preview could not be loaded</h1>
          <button type="button" onClick={() => void query.refetch()}>
            Try again
          </button>
        </main>
      );

    return <StorefrontLoading />;
  }

  const search: Record<string, string | string[]> = {};

  for (const [key, value] of new URLSearchParams(location.searchStr)) {
    const previous = search[key];
    search[key] =
      previous === undefined
        ? value
        : Array.isArray(previous)
          ? [...previous, value]
          : [previous, value];
  }

  const head = previewHead(query.data.content);
  const page = (
    <>
      {head.meta.map((meta, index) =>
        "title" in meta ? (
          <title key={index}>{meta.title}</title>
        ) : (
          <meta key={index} {...meta} />
        ),
      )}
      {head.links.map((link) => (
        <link key={`${link.rel}:${link.href}`} {...link} />
      ))}
      <PreviewPage data={query.data} search={search} />
    </>
  );
  return query.data.content.viewer ? (
    <VerifiedViewer viewer={query.data.content.viewer}>{page}</VerifiedViewer>
  ) : (
    page
  );
}
